import androidx.compose.runtime.*
import net.sergeych.ugdx.shared.*
import components.Dl
import components.RefreshIcon
import components.RoundedContainer
import components.TLink
import frontend.App
import frontend.lib.DateTimeDisplay
import frontend.lib.classList
import frontend.models.User
import frontend.pages.*
import kotlinx.coroutines.InternalCoroutinesApi
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.*

private val BATCH_SIZE = 50

@Composable
fun ServiceMessageBadge(colorClass: String, text: String) {
    Span(attrs = {
        classList("inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClass}")
    }) {
        Text(text)
    }
}

@Composable
fun ServiceMessage(msg: ServiceMessage) {
    if (msg.type == ServiceMessage.Type.INFORMATIONAL) {
        ServiceMessageBadge("bg-blue-100 text-blue-800", "info")
    }

    if (msg.type == ServiceMessage.Type.WARNING) {
        ServiceMessageBadge("bg-yellow-100 text-yellow-800", "warning")
    }

    if (msg.type == ServiceMessage.Type.ERROR) {
        ServiceMessageBadge("bg-red-100 text-red-800", "error")
    }

    Text(msg.message)
}

@Composable
fun StatusBadge(colorClass: String, text: String) {
    Span(attrs = {
        classList("inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${colorClass}")
    }) {
        Text(text)
    }
}

@Composable
fun StatusBadgeRefresh(colorClass: String, text: String, onRefresh: suspend () -> Unit) {
    val scope = rememberCoroutineScope()
    var isLoading by mutableStateOf(false)

    fun refresh() {
        scope.launch {
            isLoading = true
            onRefresh()
            isLoading = false
        }
    }

    Button(attrs = {
        classList("inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${colorClass}")
        onClick {
            refresh()
        }
        if (isLoading) disabled()
    }) {
        if (isLoading) {
            Text("Updating")
            RefreshIcon("animate-spin ml-1 h-4 w-4")
        } else {
            Text(text)
            RefreshIcon("ml-1 h-4 w-4")
        }
    }
}

@Composable
fun orderStatus(status: Order.Status, onRefresh: suspend () -> Unit) {
    when(status) {
        is Order.Status.New -> {
            StatusBadgeRefresh(
                "bg-gray-100 text-gray-800 hover:bg-gray-200",
                "New",
                onRefresh
            )
        }
        is Order.Status.Submitted -> {
            StatusBadgeRefresh(
                "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
                "Processing",
                onRefresh
            )
        }
        is Order.Status.Executed -> {
            StatusBadge("bg-green-100 text-green-800", "Executed")
        }
        is Order.Status.Rejected -> {
            StatusBadge("bg-red-100 text-red-800", "Rejected")
            Text(" ${status.message}")
        }
    }
}


@Composable
fun OrderRow(app: App, order: OrderRecord, onRefresh: suspend () -> Unit) {
    println("order msgs ${order.serviceMessages.size}")
    var status: Order.Status by remember { mutableStateOf(order.lastKnownStatus) }

//    Timer.schedule(

    Tr {
        Cell { Text(order.id.toString()) }
        Cell { Text(DateTimeDisplay(order.submittedAt)) }
        Cell {
            orderStatus(status, onRefresh)
        }
        Cell {
            if (order.order is InvoicePaymentOrder) {
                val invoiceOrder = order.order as InvoicePaymentOrder
                TLink("View invoice") {
                    app.router.dispatch("/invoices/${invoiceOrder.invoiceUniqueId}")
                }
            }
        }
        Cell {
            TLink("Details") {
                app.router.dispatch("/orders/${order.id}")
            }
        }
        Cell {
            order.serviceMessages.forEach {
                P { ServiceMessage(it) }
            }
        }
    }
}


@Composable
fun Orders(
    app: App,
    orders: List<OrderRecord>,
    hasMore: Boolean,
    onMore: () -> Unit,
    onRefresh: suspend (order: OrderRecord) -> Unit
) {
    RoundedContainer() {
        H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
            Text("Last orders")
        }

        if (orders.isEmpty()) {
            P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
                Text("You have no orders yet.")
            }
        } else {
            Table(attrs = { classList("min-w-full divide-y divide-gray-200 mt-2") }) {
                Thead(attrs = { classList("bg-gray-50") }) {
                    Tr() {
                        HeaderCell { Text("ID") }
                        HeaderCell { Text("Created at") }
                        HeaderCell { Text("Status") }
                        HeaderCell { }
                        HeaderCell { }
                        HeaderCell { Text("Service messages") }
                    }
                }

                Tbody {
                    orders.forEach {
                        OrderRow(app, it) {
                            onRefresh(it)
                        }
                    }

                    Tr() {
                        Td(attrs = { classList("px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900") }) {
                            if (hasMore) A("javascript:void(0);", attrs = {
                                classes("font-medium", "text-indigo-600", "hover:text-indigo-500")
                                onClick { onMore() }
                            }) {
                                Text("Load more")
                            }
                        }
                    }
                }
            }
        }
    }
}


@Composable
fun OrderDetails(app: App, user: User, orderId: Long) {
    var order: OrderRecord? by remember { mutableStateOf(null) }

    LaunchedEffect(true) { order = user.userAccount.getOrder(orderId) }

    order?.let { OrderDetails(app, user, it) }
}


@Composable
fun OrderDetails(app: App, user: User, order: OrderRecord) {
    var localOrder by remember { mutableStateOf(order) }

    suspend fun refresh() { localOrder = user.userAccount.getOrder(order.id) }

    Div(attrs = { classList("bg-white shadow overflow-scroll sm:rounded-lg") }) {
        Div(attrs = { classList("px-4 py-5 sm:px-6") }) {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Order ID ${order.id}")
            }
            P(attrs = { classList("mt-1 max-w-2xl text-sm text-gray-500") }) {
                TLink("Back to orders") {
                    app.router.dispatch("/orders")
                }
            }
        }
        Div(attrs = { classList("border-t border-gray-200 px-4 py-5 sm:px-6") }) {
            Dl(attrs = { classList("grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2") }) {
                Div(attrs = { classList("sm:col-span-1") }) {
                    DKey { Text("Submitted at") }
                    DValue { Text(DateTimeDisplay(order.submittedAt)) }
                }
                Div(attrs = { classList("sm:col-span-1") }) {
                    DKey { Text("Status") }
                    DValue { orderStatus(order.lastKnownStatus, ::refresh) }
                }
                Div(attrs = { classList("sm:col-span-2") }) {
                    DKey { Text("Service Messages") }
                    DValue {
                        if (order.serviceMessages.size == 0) Text("No messages")
                        else order.serviceMessages.forEach {
                            P { ServiceMessage(it) }
                        }
                    }
                }
            }
        }


    }
}


@Composable
fun OrdersScreenUI(app: App, user: User, params: MatchResult? = null) {
    val scope = rememberCoroutineScope()
    var isLoaded by remember { mutableStateOf(false) }
    var hasMore by remember { mutableStateOf(false) }
    var lastOrderId: String? by remember { mutableStateOf(null) }
    var orders = remember { mutableStateListOf<OrderRecord>() }
    var error: String? by remember { mutableStateOf(null) }
    var orderId: Long? = null

    if (params != null) {
        var (orderIdParam) = params.destructured
        orderId = orderIdParam.toLongOrNull()
    }

    suspend fun onRefresh(order: OrderRecord) {
        val newRecord = order.refresh()
        orders.set(orders.indexOf(order), newRecord)
    }

    fun onMore() {

    }

    if (!isLoaded) {
        println("Load orders screen")
//        val handler = CoroutineExceptionHandler { _, exception ->
//            error = exception.toString()
//            isLoaded = true
//            println("Coroutine exception $exception")
//        }

        scope.launch {
            try {
                val result = user.userAccount.orders(lastOrderId, BATCH_SIZE)
                orders += result.orders
                hasMore = result.hasMore
                isLoaded = true
                println(result.orders.size)
            }
            catch(err: ApiException) {
                error = err.text
                isLoaded = true
            }
            catch (err: Throwable) {
                println("$err")
                error = err.toString()
                isLoaded = true
            }

        }
    } else {
        if (orderId != null) OrderDetails(app, user, orderId)
        else {
            if (error != null) ErrorString(error ?: "")
            Orders(app, orders, hasMore, ::onMore, ::onRefresh)
        }
    }
}